import React, { Component, ReactNode, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

// ─── Lazy Chunk Retry Utility ─────────────────────────────────────────────────
/**
 * Wraps React.lazy() with automatic retry on chunk load failure.
 * Chunk load errors (ChunkLoadError / Failed to fetch dynamically imported module)
 * are the #1 cause of blank pages in production. This retries up to 3 times
 * with exponential back-off before giving up.
 */
export function retryLazy<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3,
  delay = 800
): React.LazyExoticComponent<T> {
  return React.lazy(() => {
    const attempt = (n: number): Promise<{ default: T }> =>
      importFn().catch((err) => {
        if (n <= 0) throw err;
        return new Promise((resolve) =>
          setTimeout(() => resolve(attempt(n - 1)), delay * (retries - n + 1))
        );
      });
    return attempt(retries);
  });
}

// ─── Chunk-load error detector ────────────────────────────────────────────────
function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg = error instanceof Error ? error.message : String(error);
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('ChunkLoadError') ||
    msg.includes('Loading chunk') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  );
}

// ─── Chunk file URL extractor ─────────────────────────────────────────────────
function extractChunkFile(error: unknown): string | null {
  if (!error) return null;
  const msg = error instanceof Error ? error.message : String(error);
  // "Failed to fetch dynamically imported module: https://..."
  const match = msg.match(/https?:\/\/[^\s"']+\.js/);
  return match ? match[0] : null;
}

// ─── Fire-and-forget chunk error reporter ────────────────────────────────────
function reportChunkErrorToBackend(error: unknown, reloadCount: number) {
  const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  if (!supabaseUrl) return; // Not configured — skip silently

  const endpoint = `${supabaseUrl}/functions/v1/report-chunk-error`;
  const anonKey  = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY ?? '';

  const payload = {
    page_url:      window.location.pathname + window.location.search,
    chunk_file:    extractChunkFile(error),
    error_message: error instanceof Error ? error.message : String(error),
    reload_count:  reloadCount,
    session_id:    sessionStorage.getItem('rha_session_id') ?? null,
  };

  // Fire and forget — never block the error handling flow
  fetch(endpoint, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${anonKey}`,
      'apikey':        anonKey,
    },
    body: JSON.stringify(payload),
    // keepalive ensures the request completes even if the page reloads shortly after
    keepalive: true,
  }).catch(() => {
    // Silently swallow — reporting should never interfere with error recovery
  });
}

// ─── Props / State ────────────────────────────────────────────────────────────
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

// ─── Core ErrorBoundary ───────────────────────────────────────────────────────
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught error:', error, errorInfo);
    }
    this.props.onError?.(error, errorInfo);

    // ── Auto-reload on chunk load errors ────────────────────────────────────
    if (isChunkLoadError(error)) {
      const RELOAD_KEY = 'rha_chunk_reload_count';
      const count = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);

      // Report to backend BEFORE auto-reload (keepalive ensures delivery)
      reportChunkErrorToBackend(error, count);

      if (count < 2) {
        sessionStorage.setItem(RELOAD_KEY, String(count + 1));
        setTimeout(() => window.location.reload(), 400);
        return;
      }
    }
  }

  reset() {
    this.setState({ hasError: false, error: null, isChunkError: false });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }

      // Chunk load errors get a special "reload page" prompt
      if (this.state.isChunkError) {
        return (
          <div className="w-full py-16 px-4 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-amber-50 rounded-full">
                <i className="ri-wifi-off-line text-3xl text-amber-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Page failed to load
              </h3>
              <p className="text-gray-600 mb-6">
                A network hiccup prevented this page from loading. Reload to fix it — it only takes a second.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97706] text-white rounded-lg hover:bg-[#B45309] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                Reload Page
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="w-full py-16 px-4 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-red-50 rounded-full">
              <i className="ri-error-warning-line text-3xl text-red-500"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Something went wrong
            </h3>
            <p className="text-gray-600 mb-6">
              We had trouble loading this section. Try again or go back to browsing.
            </p>
            <button
              onClick={this.reset}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97706] text-white rounded-lg hover:bg-[#B45309] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ─── Navigation-aware wrapper ─────────────────────────────────────────────────
/**
 * Wraps ErrorBoundary and resets it whenever the URL path changes.
 * This prevents a crashed page from staying crashed when the user
 * navigates away and comes back (or navigates to a different route).
 */
export function RouteErrorBoundary({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  const location = useLocation();
  const boundaryRef = useRef<ErrorBoundary>(null);
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      prevPath.current = location.pathname;
      boundaryRef.current?.reset();
    }
  }, [location.pathname]);

  return (
    <ErrorBoundary ref={boundaryRef} fallback={fallback}>
      {children}
    </ErrorBoundary>
  );
}

// ── Vehicle Grid ──────────────────────────────────────────────────────────────

export function VehicleGridErrorBoundary({ children }: { children: ReactNode }) {
  const boundaryRef = useRef<ErrorBoundary>(null);

  return (
    <ErrorBoundary
      ref={boundaryRef}
      fallback={<VehicleGridFallback onRetry={() => boundaryRef.current?.reset()} />}
    >
      {children}
    </ErrorBoundary>
  );
}

function VehicleGridFallback({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="w-full py-12 px-4">
      <div className="max-w-2xl mx-auto text-center">
        <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-amber-50 rounded-full">
          <i className="ri-car-line text-4xl text-[#D97706]"></i>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          Couldn&apos;t Load Vehicles
        </h3>
        <p className="text-gray-600 mb-8">
          Something went wrong while loading inventory. Hit retry and it should come right back.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={onRetry}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#D97706] text-white rounded-lg hover:bg-[#B45309] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Retry
          </button>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-customer-service-2-line"></i>
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Vehicle Detail ────────────────────────────────────────────────────────────

export function VehicleDetailErrorBoundary({ children }: { children: ReactNode }) {
  const boundaryRef = useRef<ErrorBoundary>(null);

  return (
    <ErrorBoundary
      ref={boundaryRef}
      fallback={<VehicleDetailFallback onRetry={() => boundaryRef.current?.reset()} />}
    >
      {children}
    </ErrorBoundary>
  );
}

function VehicleDetailFallback({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="w-full min-h-screen flex items-center justify-center px-4 py-16">
      <div className="max-w-2xl mx-auto text-center">
        <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center bg-amber-50 rounded-full">
          <i className="ri-car-line text-5xl text-[#D97706]"></i>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Vehicle Unavailable
        </h1>
        <p className="text-lg text-gray-600 mb-8">
          We couldn&apos;t load this vehicle&apos;s details. It may have just sold — browse our current inventory to find something similar.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/inventory"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97706] text-white rounded-lg hover:bg-[#B45309] transition-colors text-lg font-medium cursor-pointer whitespace-nowrap"
          >
            <i className="ri-car-line"></i>
            Browse All Vehicles
          </Link>
          <button
            onClick={onRetry}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors text-lg font-medium cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
}

// ── App-level boundary (catches uncaught render errors) ───────────────────────

interface AppErrorState {
  hasError: boolean;
  isChunkError: boolean;
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, AppErrorState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): AppErrorState {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidMount() {
    // ── Global unhandled promise rejection handler ──
    // ErrorBoundary only catches synchronous render errors.
    // Async errors (failed fetches, rejected promises) bypass it entirely.
    // This catches them and logs in dev so they don't silently swallow bugs.
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    // Ignore intentional aborts (fetch cancellations, Supabase aborts)
    const reason = event.reason;
    const isAbort =
      reason instanceof DOMException && reason.name === 'AbortError' ||
      (reason instanceof Error && reason.message.includes('AbortError')) ||
      (reason && typeof reason === 'object' && 'code' in reason && (reason as any).code === '20') ||
      (reason instanceof Error && reason.message === 'Request cancelled');

    if (isAbort) return;

    if (import.meta.env.DEV) {
      console.error('[AppErrorBoundary] Unhandled promise rejection:', reason);
    }
    // Don't setState here — async errors shouldn't blank the whole app.
    // They are handled at the component level (useVehicles, useVehicleById, etc.)
  };

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('[AppErrorBoundary]', error, info);
    }

    // ── Auto-reload on chunk load errors ────────────────────────────────────
    if (isChunkLoadError(error)) {
      const RELOAD_KEY = 'rha_chunk_reload_count';
      const count = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);

      // Report to backend before reload
      reportChunkErrorToBackend(error, count);

      if (count < 2) {
        sessionStorage.setItem(RELOAD_KEY, String(count + 1));
        setTimeout(() => window.location.reload(), 400);
        return;
      }
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0] px-4">
            <div className="max-w-md w-full text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-amber-50 rounded-full">
                <i className="ri-wifi-off-line text-4xl text-amber-500"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">
                Page failed to load
              </h1>
              <p className="text-gray-600 mb-8">
                A network issue prevented the page from loading. This usually fixes itself with a quick reload.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97706] text-white rounded-lg hover:bg-[#B45309] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                Reload Page
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0] px-4">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-amber-50 rounded-full">
              <i className="ri-car-line text-4xl text-[#D97706]"></i>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-8">
              An unexpected error occurred. Reload the page to get back on the road.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97706] text-white rounded-lg hover:bg-[#B45309] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
